// Import debounce
import debounce from 'debounce';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// eslint-disable-next-line import/no-unresolved
import $ from 'jquery';

import {
	AJAXRequest,
	getUnsavedFieldsValues,
	getField,
	isCustomValue
} from '@automator/builder/_legacy/js/common/utilities';

import { sanitizeFieldAttributes } from '@automator/builder/_legacy/js/field/utilities';

import Field from './index.js';

// const sortable = require( 'html5sortable' );

class FieldsRepeater {
	// eslint-disable-next-line no-unused-vars
	constructor( attributes, createDOMelements = true, customValidationFunction = null ) {
		// Save attributes
		this.setAttributes( attributes );

		// Create fields
		this.createFields();
	}

	/**
	 * Set attributes. It add properties like hasValue, hasPlaceholder, etc, automatically
	 *
	 * @since 0.4
	 * @param {object} attributes - The field attributes
	 * @return {object} attributes - The field attributes
	 */
	setAttributes( attributes ) {
		// Has label
		if ( ! isDefined( attributes.hasLabel ) ) {
			if ( ! isEmpty( attributes.label ) ) {
				attributes.hasLabel = true;
			} else {
				attributes.hasLabel = false;
			}
		}

		// Has description
		if ( ! isDefined( attributes.hasDescription ) ) {
			if ( ! isEmpty( attributes.description ) ) {
				attributes.hasDescription = true;
			} else {
				attributes.hasDescription = false;
			}
		}

		// Default token name
		if ( isEmpty( attributes.tokenName ) ) {
			attributes.tokenName = attributes.label;
		}

		// Check if we should sort the rows
		if ( ! isDefined( attributes.canSortRows ) ) {
			attributes.canSortRows = true;
		}

		// Check if the layout is defined
		if ( ! isDefined( attributes.layout ) ) {
			attributes.layout = 'default';
		}

		// Check if we have to hide the header
		if ( ! isDefined( attributes.hide_header ) ) {
			attributes.hide_header = false;
		}

		this.attributes = attributes;
	}

	/**
	 * Create instances of Field
	 *
	 * @since  2.0
	 * @return undefined
	 */
	createFields() {
		// Get fields template
		const rowFieldsTemplate = this.attributes.fields;

		// Create array with fields
		let fieldsRows = [];

		// Check if there is data and create multiple rows of the repeater field
		if ( Array.isArray( this.attributes.currentValue ) && this.attributes.currentValue.length > 0 ) {
			// https://regex101.com/r/tLL7S9/1/
			// Get field id and index from strings like FIELD_1

			// Iterate default rows
			this.attributes.currentValue.forEach( ( row ) => {
				// Clone template row
				let rowFields = JSON.parse( JSON.stringify( rowFieldsTemplate ) );

				// If there aren't fields, and this is using AJAX, create fields using the current value, instead of iterating the fields
				if ( isDefined( this.attributes.ajax ) && isEmpty( rowFieldsTemplate ) ) {
					rowFields = Object.entries( row )
						.filter( ( [ optionCode ] ) => {
							return ! optionCode.endsWith( '_readable' );
						} )
						.map( ( [ optionCode, value ] ) => {
							// Create field
							return {
								fieldType: 'text',
								optionCode: optionCode,
								currentValue: value,
								itemId: this.attributes.itemId,
								isReadOnly: true,
							};
						} );
				} else {
					// Iterate the rows in the template and try to get their value from the "row" array
					rowFields = rowFields.map( ( field ) => {
						// Check if there is a value for this field
						if ( isDefined( row[ field.optionCode ] ) ) {
							// Save value
							field.currentValue = row[ field.optionCode ];

							// Check if there is a custom value and if we should add it
							if ( 
								isCustomValue( field.currentValue ) &&
								isDefined( row[ `${ field.optionCode }_custom` ] )
							) {
								field.customValue = row[ `${ field.optionCode }_custom` ];
							}
						} else {
							// Set empty value
							field.currentValue = '';
						}

						// Return field
						return field;
					} );
				}

				// Add row with fields
				fieldsRows.push( rowFields );
			} );
		} else if ( this.attributes.isRequired ) {
			// Otherwise, just create one row
			// Check if the field is required
			//
			// Create an empty row
			const emptyRow = JSON.parse( JSON.stringify( rowFieldsTemplate ) );

			// Remove undefined current value
			emptyRow.currentValue = '';

			// Add it to the main array
			fieldsRows.push( emptyRow );
		}

		// Now iterate the row with fields
		fieldsRows = fieldsRows.map( ( row, index ) => {
			// Now iterate each group of fields
			row = row.map( ( fieldObject ) => {
				// Remove the label
				// fieldObject.label = '';

				// Remove the description
				fieldObject.description = '';

				// Add item ID
				fieldObject.itemId = this.attributes.itemId;
				fieldObject.parentID = this.attributes.parentID;

				// If the value is an array, change the field type to select
				if ( 
					fieldObject.fieldType === 'text' && 
					Array.isArray( fieldObject.currentValue )
				) {
					fieldObject.fieldType = 'select';
				}

				// Create field
				const field = new Field( fieldObject.fieldType, {
					...fieldObject,
					...{
						repeaterOptionCode: this.attributes.optionCode,
						repeaterRowIndex: index,
						itemType: this.attributes.itemType,
						itemId: this.attributes.itemId,
					}
				} );

				// AJAX: Check if it's the mapping column
				field.isMappingColumn = isDefined( this.attributes.ajax ) && 
										isDefined( this.attributes.ajax.mappingColumn ) && 
										field.getFieldID() === this.attributes.ajax.mappingColumn;

				// Return field instance
				return field;
			} );

			// Return row
			return row;
		} );

		// Update the fields property
		this.fieldRows = fieldsRows;
	}

	/**
	 * Create a jQuery DOM element with the field
	 *
	 * @since 0.3
	 */
	createFormElement( isRerendering = false ) {
		// Create response object
		let response = null;

		// Create container
		const $container = $( `<div class="form-repeater"></div>` );

		// Check if it has a label
		if ( this.attributes.hasLabel ) {
			// Create label
			const $label = $( `
				<div class="form-repeater__label">
					${ this.attributes.label }
					${ this.attributes.isRequired ? `<span class="form-repeater__label-required"></span>` : `` }
				</div>
			` );

			// Add button to manually trigger AJAX call again
			if (
				isDefined( this.attributes.ajax ) && 
				isDefined( this.attributes.ajax.event ) && 
				[ 'parent_fields_change', 'on_load' ].includes( this.attributes.ajax.event )
			) {
				$label.get(0).insertAdjacentHTML( 'beforeEnd', `
					<uo-button
						color="secondary"
						size="extra-small"
						class="form-element-ajax-refresh-btn"
					>
						<uo-icon id="rotate"></uo-icon>

						${ escapeHTML( __( 'Refresh', 'uncanny-automator' ) ) }
					</uo-button>
				` );
			}

			// Append label
			$container.append( $label );
		}

		// Create table with the fields
		const $table = this.getFieldsTable();
		$container.append( $table );

		// Add error container
		$table.append( $( `<div class="form-element__error"></div>` ) );

		// Check if it has a description
		if ( this.attributes.hasDescription ) {
			// Create description
			const $description = $( `<div class="form-repeater__description">${ this.attributes.description }</div>` );

			// Append description
			$container.append( $description );
		}

		// Save form element
		this.formElement = $container;
		response = $container;

		// If there is more than one row, make list sortable
		// if ( this.fieldRows.length > 1 ) {
		// 	this.makeRowsSortable();
		// }

		// Listen save
		this.listenSave();

		// Listen fields for AJAX
		this.handleAJAX( {
			isRerendering: isRerendering
		} );

		if ( this.attributes.dynamic_visibility ) {
			this.applyDynamicVisibility();
			this.setupVisibilityChangeListener();
		}

		// Return form element
		return response;
	}

	/**
	 * Gets the data needed by the server when doing AJAX requests from the field
	 * 
	 * @param {Object} options 
	 * @param {String} options.fieldToListenToCode The ID of the field that triggered the AJAX call
	 * @returns {Object} The data to be sent to the server when doing an AJAX call made by the field
	 */
	getAjaxRequestData( { fieldToListenToCode } ) { 
		return {
			recipe_id: UncannyAutomator._recipe.recipe_id,
			parent_id: this.attributes.parentID,
			item_id: this.attributes.itemId,
			group_id: this.attributes.groupId,
			field_id: this.attributes.optionCode,
			triggered_by: fieldToListenToCode,
			values: getUnsavedFieldsValues( { 
				itemType: this.attributes.itemType,
				itemID: this.attributes.itemId, 
				field: this 
			} ),
		}
	}

	/**
	 * Merges two objects with fields data, keeping values as much as possible, assuming the values in the mapping column are unique
	 * 
	 * @param {Array} oldValues The old values
	 * @param {Array} newValues The new values
	 * @returns {Array} The new values with the old values merged
	 */
	getMergedValuesWithMappingColumn( oldValues, newValues ) {
		if ( ! isDefined( this.attributes.ajax.mappingColumn ) ) {
			return newValues;
		}

		// Map old rows using the mapping column
		const oldRowsMappingColumn = {}

		oldValues.forEach( row => {
			const mappingValue = row[ this.attributes.ajax.mappingColumn ];

			// Check if the mapping column has a value
			if ( isEmpty( mappingValue ) ) {
				return;
			}

			delete row[ this.attributes.ajax.mappingColumn ];
			oldRowsMappingColumn[ mappingValue ] = row;
		} );
		
		newValues = newValues.map( ( row ) => {
			// Try to get the old values
			const oldValues = isDefined( oldRowsMappingColumn[ row[ this.attributes.ajax.mappingColumn ] ] ) ? oldRowsMappingColumn[ row[ this.attributes.ajax.mappingColumn ] ] : {};

			return { ...row, ...oldValues };
		} );

		return newValues;
	}

	/**
	 * Returns the mapping column that's used to update the field
	 */
	get ajaxMappingColumn() {
		if ( ! isDefined( this.attributes.ajax ) ) {
			return;
		}

		if ( ! isDefined( this.attributes.ajax.mappingColumn ) ) {
			return;
		}
		
		return this.attributes.ajax.mappingColumn;
	}

	/**
	 * Handles the AJAX functionality on fields
	 * Fields will listen to changes on other fields and trigger an AJAX call to retrieve a value or dropdown options
	 */
	handleAJAX( { isRerendering } ) {
		// Check if we should listen to other fields
		if ( ! isDefined( this.attributes.ajax ) || ! isDefined( this.attributes.ajax.event ) ) {
			return;
		}

		/**
		 * Performs the AJAX call
		 */
		const doAJAX = ( { fieldToListenToCode = '', onSuccess = () => {}, extraAttributes = {} } ) => {
			// Save the old values
			const oldRows = this.getValue( { includeReadOnly: false } );

			// Combines the data from an AJAX request with additional attributes.
			const ajaxValues = { 
				...this.getAjaxRequestData( { fieldToListenToCode } ),
				...extraAttributes
			};

			// Before starting, clear any errors
			// This will hide errors from previous calls before doing the new one
			this.clearError();

			AJAXRequest(
				this.attributes.ajax.endpoint,
				ajaxValues,
				( response ) => {
					// We'll update this even if the call returns an error. The call itself was successful
					this.attributes.ajax.firstLoadDone = true;

					try {
						onSuccess();
					} catch ( e ) {}
					
					// Check if the call was successful
					if ( isDefined( response.success ) && ! response.success ) {
						// Reset value
						this.setValue( [] );

						// Check if there is a message available
						if ( ! isEmpty( response.error ) ) {
							this.setError( response.error );
						}

						return;
					}

					// Check if we have to update the field attributes
					if ( isDefined( response.field_properties ) ) {
						// Check if it has fields
						if ( isDefined( response.field_properties.fields ) ) {
							// Sanitize with sanitizeFieldAttributes
							response.field_properties.fields = response.field_properties.fields.map( field => {
								return sanitizeFieldAttributes( field );
							} );
						}

						this.updateAttributes( 
							response.field_properties, 
							// Don't render the field again (yet)
							false
						);
					}

					// If it's defined, save the new values
					if ( isDefined( response.rows ) ) {
						// Set rows, but try to keep the previous values
						this.setValue( 
							this.getMergedValuesWithMappingColumn( oldRows, response.rows ),
							// Don't render the field again (yet)
							false
						);
					}

					// Render the field again now
					this.reRender();
				}
			);
		}

		// Check if we have to listen other fields
		if ( this.attributes.ajax.event === 'parent_fields_change' ) {
			// Parent change event
			const parentChangeEvent = `automator:field:change.handle-ajax-${ this.attributes.optionCode }`;

			// Check if the fields exist
			if ( ! isDefined( this.attributes.ajax.listenFields ) && Array.isArray( this.attributes.ajax.listenFields ) ) {
				return;
			}

			this.attributes.ajax.listenFields.forEach( ( fieldID ) => {
				// Get field
				const fieldToListenTo = getField( this.attributes.itemId, fieldID, this.attributes.itemType );

				// Get the debounce delay based on the field type
				const debounceDelay = [ 'date', 'time', 'select', 'radio', 'checkbox', 'file' ].includes( fieldToListenTo.type ) ? 0 : 1000;

				// Listen changes to the parent field
				fieldToListenTo.formElement.off( parentChangeEvent );
				fieldToListenTo.formElement.on( parentChangeEvent, debounce( ( event, context ) => {
					// Don't trigger when getting formElement
					if ( isDefined( context ) && isDefined( context.source ) && context.source === 'getFormElement' ) {
						return;
					} 

					// Before doing the request, show a loading animation in the field that changed
					fieldToListenTo.startLoadingAnimation();
					// And show animation in the target field too
					this.startLoadingAnimation();

					doAJAX( {
						extraAttributes: {
							context: 'parent-field-changed'
						},
						fieldToListenToCode: fieldToListenTo.attributes.optionCode,
						onSuccess: () => {
							// Stop loading animation in both fields
							fieldToListenTo.stopLoadingAnimation();
							this.stopLoadingAnimation();
						}
					} );
				}, debounceDelay ) );

				// Check if it's a dropdown that supports custom values
				if ( fieldToListenTo.type === 'select' && fieldToListenTo.attributes.supportsCustomValue ) {
					fieldToListenTo.customValueField.formElement.off( parentChangeEvent );
					fieldToListenTo.customValueField.formElement.on( 'automator:field:change', debounce( ( event, context ) => {
						// Don't trigger when getting formElement
						if ( isDefined( context ) && isDefined( context.source ) && context.source === 'getFormElement' ) {
							return;
						}

						// Before doing the request, show a loading animation in the field that changed
						fieldToListenTo.startLoadingAnimation();
						// Show loading animation in the custom value too
						fieldToListenTo.customValueField.startLoadingAnimation();
						// And show animation in the target field too
						this.startLoadingAnimation();
	
						doAJAX( {
							extraAttributes: {
								context: 'parent-field-changed'
							},
							fieldToListenToCode: fieldToListenTo.attributes.optionCode,
							onSuccess: () => {
								// Stop loading animation in all fields
								fieldToListenTo.stopLoadingAnimation();
								fieldToListenTo.customValueField.stopLoadingAnimation();
								this.stopLoadingAnimation();
							}
						} );
					}, 1000 ) );
				}
				
				// Trigger change event in the parent field as the field load
				// But do it only if the parent field isn't listening to another field too
				if ( ! isRerendering && ! isDefined( fieldToListenTo.attributes.ajax ) && isDefined( fieldToListenTo.field ) ) {
					fieldToListenTo.field.trigger( 'automator:field:change' );
				}
			} );

			// Check if the "Refresh" button exists and add event listener
			if ( this.$ajaxRefreshBtn ) {
				this.$ajaxRefreshBtn.addEventListener( 'click', () => {
					// Show animation in the target field
					this.startLoadingAnimation();

					doAJAX( {
						extraAttributes: {
							context: 'refresh-button'
						},
						fieldToListenToCode: this.attributes.optionCode,
						onSuccess: () => {
							// Stop loading animation 
							this.stopLoadingAnimation();
						}
					} );
				} );
			}
		}

		// Check if we have to perform the call on load
		if ( this.attributes.ajax.event === 'on_load' ) {
			// Do the initial AJAX call only if it's NOT rerendering.
			// The initial call has to be done only when the field is added to the DOM
			if ( ! isRerendering ) { 
				// Show animation in the target field
				this.startLoadingAnimation();

				doAJAX( {
					extraAttributes: {
						context: 'on-load'
					},
					fieldToListenToCode: this.attributes.optionCode,
					onSuccess: () => {
						// Stop loading animation 
						this.stopLoadingAnimation();
					}
				} );
			}

			// Check if the "Refresh" button exists and add event listener
			if ( this.$ajaxRefreshBtn ) {
				this.$ajaxRefreshBtn.addEventListener( 'click', () => {
					// Show animation in the target field
					this.startLoadingAnimation();

					doAJAX( {
						extraAttributes: {
							context: 'refresh-button'
						},
						fieldToListenToCode: this.attributes.optionCode,
						onSuccess: () => {
							// Stop loading animation 
							this.stopLoadingAnimation();
						}
					} );
				} );
			}
		} 
	}

	/**
	 * Listen save
	 */
	listenSave() {
		// Get form element
		// eslint-disable-next-line no-unused-vars
		this.formElement.on( 'saved', ( event ) => {
			// Update the fields array
			this.updateAttributesFieldsFromRow();
		} );
	}

	/**
	 * Use fieldRows to update the original attributes.fields
	 * We will use this to render the container again after it was
	 * saved
	 */
	updateAttributesFieldsFromRow() {
		// Create the array where we're going to save the fields data
		const fields = [];

		// Iterate fieldRows
		this.fieldRows.forEach( ( rowFields ) => {
			// Create array to save the row fields
			const row = [];

			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				row.push( fieldInstance.attributes );
			} );

			// Add row
			fields.push( row );
		} );

		// Update fields
		this.attributes.fields = fields;
	}

	/**
	 * Make list of rows sortable
	 */
	// makeRowsSortable() {
	// 	// Check if we should make the rows sortable
	// 	if ( this.attributes.canSortRows && this.attributes.layout === 'default' ) {
	// 		// Make rows sortable
	// 		// Use sortable from the html5sortable library
	// 		sortable( this.formElement.find( '.form-repeater-fields__table-rows' ), {
	// 			forcePlaceholderSize: true,
	// 			handle: '.form-repeater-fields__table-rows-cell--number',
	// 			placeholder: `<tr><td colspan="${ this.fieldRows[ 0 ].length + 2 }">&nbsp;</td></tr>`,
	// 			// eslint-disable-next-line no-unused-vars
	// 			itemSerializer: ( serializedItem, sortableContainer ) => {
	// 				return {
	// 					position: serializedItem.index,
	// 					html: serializedItem.html,
	// 				};
	// 			},
	// 		} );

	// 		// Listen modifications to the rows order
	// 		this.formElement.find( '.form-repeater-fields__table-rows' ).on( 'sortupdate', ( event ) => {
	// 			// Create an array where each element is the index of the row in the old order
	// 			const newOrder = [];

	// 			// Iterate the rows (in their new order)
	// 			$.each( event.detail.destination.items, ( index, row ) => {
	// 				newOrder.push( $( row ).data( 'order' ) );
	// 			} );

	// 			// Change the order of the rows in the FieldRepeater instance
	// 			const newFieldRowsOrder = [];
	// 			newOrder.forEach( ( oldIndex ) => {
	// 				newFieldRowsOrder.push( this.fieldRows[ oldIndex ] );
	// 			} );
	// 			this.fieldRows = newFieldRowsOrder;

	// 			// Render the field agian
	// 			this.reRender();
	// 		} );
	// 	}
	// }

	/**
	 * Update the field type and updates the attributes
	 * 
	 * @beta This method is in beta and might change in the future
	 * 
	 * @param {Object} newAttributes The new attributes
	 * @param {Boolean} reRender TRUE if you want to re-render the field, FALSE if you only want to update the attributes
	 */
	updateAttributes( newAttributes = {}, reRender = true ) {
		const updatedAttributes = {
			...this.attributes,
			...newAttributes
		};

		// Update the attributes
		this.setAttributes( updatedAttributes );

		// Rerender
		if ( reRender ) {
			this.reRender();
		}
	}

	/**
	 * Create and return fields table
	 *
	 * @since  2.0
	 */
	getFieldsTable() {
		// Create the CSS classes
		const CSSclasses = [
			`form-repeater-fields`,
			`form-repeater-fields--${ this.attributes.layout }`
		];

		// Check if we have to hide the header
		if ( this.attributes.hide_header ) {
			CSSclasses.push( 'form-repeater-fields--hide-header' );
		}

		// Check if we have to hide the actions
		if ( this.attributes.hideActions ) {
			CSSclasses.push( 'form-repeater-fields--hide-actions' );
		}

		// Check if it's an AJAX field
		if ( isDefined( this.attributes.ajax ) ) {
			CSSclasses.push( 'form-repeater-fields--ajax' );
		}

		// Check if it has no fields
		if ( this.attributes.fields.length === 0 ) {
			CSSclasses.push( 'form-repeater-fields--no-fields' );
		}

		// Create variable to save the table
		const $table = $( `
			<div class="${ CSSclasses.join( ' ' ) }" data-id="${ this.attributes.optionCode }">
				<table class="form-repeater-fields__table">
					<thead class="form-repeater-fields__table-heading"></thead>
					<tbody class="form-repeater-fields__table-rows"></tbody>
				</table>
			</div>
		` );

		// Check if we have to render the actions
		if ( ! this.attributes.hideActions ) {
			$table.append( $( `<div class="form-repeater-fields__actions"></div>` ) );
		}

		// Get the table content
		const tableMatrix = this.attributes.layout === 'default' ? this.tableMatrix : this._transposeMatrix( this.tableMatrix );

		// Check if we have to render the header
		if ( ! this.attributes.hide_header ) {
			// Create the table header
			$table.find( '.form-repeater-fields__table-heading' )
				.append( this._templateHeader( tableMatrix[ 0 ] ) );
		}

		// Create HTML of the rows
		$table.find( '.form-repeater-fields__table-rows' )
			.append( this._templateRows( tableMatrix.slice( 1 ) ) );

		// Create "Add row" button
		const fieldAtts = this.getAttributes();
		// if hideActions is false, Add Pair button
		if ( ! fieldAtts.hideActions ) {
			const $addRowButton = $( `
				<div class="uap-btn uap-btn--primary uap-btn--small">
					${ escapeHTML( __( 'Add row', 'uncanny-automator' ) ) }
				</div>
			` );

			// Check if we have to change label of the button
			if ( isDefined( this.attributes.addRowButtonLabel ) ) {
				$addRowButton.text( this.attributes.addRowButtonLabel );
			}

			// Listen clicks
			$addRowButton.on( 'click', () => {
				// Show loading animation
				$addRowButton.addClass( 'uap-btn--loading' );

				// Add another row
				this.addRow();
			} );

			// Add actions
			const $actionsContainer = $table.find( '.form-repeater-fields__actions' );
			$actionsContainer.append( $addRowButton );
		}
		return $table;
	}

	/**
	 * Get value
	 *
	 * @since  4.9
	 * @return {Array|Object} Array of objects, where the key is the field ID and the value is the field value
	 */
	getValue( options = {} ) {
		options = { ...{
			includeReadOnly: true
		}, ...options }

		// Create object where we're going to save the values
		const value = [];

		// Iterate rows
		this.fieldRows.forEach( ( row ) => {
			// Get the value of the fields
			const fieldsValue = {};

			// Iterate fields
			row.forEach( ( fieldInstance ) => {
				// Check if the field is editable and if we have to include it
				if ( 
					// Check if we should NOT include the values from read-only fields
					! options.includeReadOnly && 
					// If that's the case, check if the field is read-only
					fieldInstance.attributes.isReadOnly && 
					// BUT, even if it's read only, still include if it's the mapping column
					! fieldInstance.isMappingColumn
				) {
					return;
				}

				// Get the field value
				const fieldInstanceValue = fieldInstance.getValue();

				// Add it to the fieldsValue object
				fieldsValue[ fieldInstance.getFieldID() ] = fieldInstanceValue;

				// Save the readable value
				if ( fieldInstance.type == 'select' ) {
					// Get the option text
					// eslint-disable-next-line camelcase
					fieldsValue[ `${ fieldInstance.getFieldID() }_readable` ] = fieldInstance.readableValue;
				}

				// Check if we have to save the custom value (only for selects)
				if (
					fieldInstance.type == 'select' &&
					isCustomValue( fieldInstanceValue ) 
				) {
					try {
						// Save the custom value
						fieldsValue[ `${ fieldInstance.getFieldID() }_custom` ] = fieldInstance.getSelectCustomValue();

						// Get the option text
						// eslint-disable-next-line camelcase
						fieldsValue[ `${ fieldInstance.getFieldID() }_readable` ] = fieldsValue[ `${ fieldInstance.getFieldID() }_custom` ];
					} catch ( e ) {
						console.warn( `Couldn't save the custom value` );
					}
				}
			} );

			// Add fields value to the main array
			value.push( fieldsValue );
		} );

		// Return value
		return value;
	}

	/**
	 * Returns the field value
	 * 
	 * @return {String} The field value
	 */
	get value() {
		return this.getValue();
	}

	/**
	 * Returns the value from the meta
	 */
	get valueFromMeta() {
		let value = null;

		// Check if the field has a value and add it
		if ( this.attributes.hasValue ) {
			// Return that value
			value = this.attributes.currentValue;
		} else if ( this.attributes.hasDefaultValue ) {
			// Return the default value
			value = this.attributes.defaultValue;
		}

		return value;
	}

	/**
	 * Set value
	 * 
	 * @param {Array} newRows The new rows that have to be created
	 * @param {Boolean} reRender TRUE if you want to re-render the field, FALSE if you only want to update the attributes
	 * 
	 * @since 4.9
	 */
	setValue( newRows = [], reRender = true ) {
		// Remove all the current rows
		this.fieldRows = [];

		// Iterate rows
		newRows.forEach( ( row, index ) => {
			this.addRow( row, false);
		} );

		// Render again
		if ( reRender ) {
			this.reRender();
		}
	}

	/**
	 * Add row
	 *
	 * @since  2.0
	 */
	addRow( fieldsData = {}, reRender = true ) {
		// Create an empty row
		let emptyRow = JSON.parse( JSON.stringify( this.attributes.fields ) );

		// Now iterate the fields
		emptyRow = emptyRow.map( ( fieldObject ) => {
			// Check if there is a default value for this field
			if ( isDefined( fieldsData[ fieldObject.optionCode ] ) ) {
				// And if there is, use that data
				fieldObject.currentValue = fieldsData[ fieldObject.optionCode ];
			}

			// Create field
			const field = new Field( 
				fieldObject.fieldType, 
				{ 
					...fieldObject, 
					...{
						parentID: this.attributes.parentID,
						itemId: this.attributes.itemId,
						itemType: this.attributes.itemType,
						repeaterOptionCode: this.attributes.optionCode,
					} 
				}
			);

			// AJAX: Check if it's the mapping column
			field.isMappingColumn = field.getFieldID() === this.ajaxMappingColumn;

			// Return field instance
			return field;
		} );

		// Add it to the rows of fields
		this.fieldRows.push( emptyRow );

		// Render form element again
		if ( reRender ) {
			this.reRender( true );
		}
	}

	/**
	 * Rerender the field again
	 */
	reRender( focusFirstFieldOfLastRow = false ) {
		// Check if we need to recreate fields after AJAX update.
		if ( this._needsFieldRecreation() ) {
			// Recreate fields.
			this.createFields();
		} else {
			// Standard re-render: recreate fields from current values
			this.fieldRows = this._createFieldRowsFromValues( this.value );
		}

		// Create the form element again
		const $currentFormElement = this.formElement;
		const $newFormElement = this.createFormElement( true );
		$currentFormElement.replaceWith( $newFormElement );

		// Create the date/time selector
		this.initFlatpickr();

		// Create the token selector
		this.initTokenSelector();

		// Create the CodeMirror instances
		this.initCodeMirror();

		// Check if we have to focus the first field of the last row
		if ( focusFirstFieldOfLastRow ) {
			// Get the last row
			const lastRow = this.fieldRows[ this.fieldRows.length - 1 ];

			if ( isDefined( lastRow ) ) {
				// Get the first field
				const firstField = lastRow[ 0 ];

				if ( isDefined( firstField ) ) {
					firstField.focus();
				}
			}
		}
	}

	/**
	 * Checks if we need to recreate fields after AJAX update
	 * 
	 * @returns {Boolean}
	 */
	_needsFieldRecreation() {
		// Check if we have fields defined, no existing rows 
		// and actions are hidden (indicating auto-population is expected).
		return this.attributes.fields.length > 0 && 
			this.fieldRows.length === 0 && 
			this.attributes.hideActions;
	}

	/**
	 * Creates field rows from values with proper custom value handling
	 * 
	 * @param {Array} values - Array of row values
	 * @returns {Array} Array of field rows
	 */
	_createFieldRowsFromValues( values ) {
		return values.map( ( rowValues, index ) => {
			// Create an empty row
			let newRow = JSON.parse( JSON.stringify( this.attributes.fields ) );

			// Now iterate the fields
			newRow = newRow.map( ( fieldObject ) => {
				return this._createFieldInstance( fieldObject, rowValues, index );
			} );

			return newRow;
		} );
	}

	/**
	 * Creates a single field instance with proper custom value handling
	 * 
	 * @param {Object} fieldObject - Field configuration object
	 * @param {Object} rowValues - Row values object
	 * @param {Number} index - Row index
	 * @returns {Field} Field instance
	 */
	_createFieldInstance( fieldObject, rowValues, index ) {
		// Get the current value
		// For textareas, preserve original value to avoid newline loss during DOM re-renders
		// @todo If you open the repeater field, modify it, but don't save and drag the field, it will loose value (untested)
		const isTextarea = 'textarea' === fieldObject.fieldType;
		const originalRowValues = this.attributes.currentValue?.[ index ];
		const shouldPreserveOriginal = isTextarea && isDefined( originalRowValues );
		
		const currentValue = shouldPreserveOriginal 
			? originalRowValues[ fieldObject.optionCode ]
			: rowValues[ fieldObject.optionCode ];
		
		// Check if there's a custom value
		const customValueKey = `${ fieldObject.optionCode }_custom`;
		const customValue = isDefined( rowValues[ customValueKey ] ) 
			? rowValues[ customValueKey ] 
			: undefined;
		
		// Create field with proper custom value handling
		const field = new Field( fieldObject.fieldType, { 
			...fieldObject, 
			...{
				currentValue: currentValue,
				customValue: customValue,
				parentID: this.attributes.parentID,
				itemId: this.attributes.itemId,
				itemType: this.attributes.itemType,
				repeaterOptionCode: this.attributes.optionCode,
				repeaterRowIndex: index
			} 
		} );

		// AJAX: Check if it's the mapping column
		field.isMappingColumn = field.getFieldID() === this.ajaxMappingColumn;

		// Return field instance
		return field;
	}

	/**
	 * Delete row
	 *
	 * @since  2.0
	 */
	deleteRow( index ) {
		// Check if there is a row with that index
		if ( isDefined( this.fieldRows[ index ] ) ) {
			// Remove element
			this.fieldRows.splice( index, 1 );
		}

		// Render form element again
		this.formElement.replaceWith( this.createFormElement( true ) );
	}

	/**
	 * Shows an error in the field
	 * 
	 * @param {String} message The message to clear the error
	 */
	setError( message = '' ) {
		// Make sure there is a message defined
		if ( isEmpty( message ) ) {
			return;
		}

		// Add error classes
		this.$errorWrapper.innerHTML = message;

		// Add error class to the main wrapper
		this.formElement.get(0).classList.add( 'form-element--error' );
	}

	/**
	 * Removes the error from a field
	 */
	clearError() {
		// Remove message
		this.$errorWrapper.innerHTML = '';

		// Remove error class (hide error container too)
		this.formElement.get(0).classList.remove( 'form-element--error' );
	}

	/**
	 * Adds loading animation to field
	 *
	 * @since 4.9
	 */
	startLoadingAnimation() {
		// Check if has class
		const CSSclass = 'form-element--loading';

		if ( isDefined( this.formElement ) && ! this.formElement.hasClass( CSSclass ) ) {
			// Add class
			this.formElement.addClass( CSSclass );

			// Trigger
			this.formElement.trigger( 'automator:field:loading' );
		}
	}

	/**
	 * Removes loading animation to field
	 *
	 * @since 4.9
	 */
	stopLoadingAnimation() {
		// Check if has class
		const CSSclass = 'form-element--loading';

		if ( isDefined( this.formElement ) && this.formElement.hasClass( CSSclass ) ) {
			// Remove class
			this.formElement.removeClass( CSSclass );

			// Remove checkbox class
			this.fieldContainer.removeClass( 'form-checkbox--loading-inline' );

			// Trigger
			this.formElement.trigger( 'automator:field:loading' );
		}
	}

	/**
	 * Returns the container that shows the error message
	 */
	get $errorWrapper() {
		return this.formElement.get(0).querySelector( '.form-element__error' );
	}

	/**
	 * Returns button to manually retry AJAX call
	 */
	get $ajaxRefreshBtn() {
		return this.formElement.get(0).querySelector( '.form-element-ajax-refresh-btn' );
	}

	/**
	 * Get form element
	 *
	 * @since 2.0
	 * @return {object} jQuery object of the field's container
	 */
	getFormElement() {
		// Reset the "first load" status
		this.attributes.completedFirstLoad = false;

		// Return form element
		return this.formElement;
	}

	/**
	 * Get attributes
	 *
	 * @since 2.0
	 * @return {object} Field attributes
	 */
	getAttributes() {
		return this.attributes;
	}

	/**
	 * Get field ID
	 *
	 * @since 2.0
	 * @return {String} The field ID
	 */
	getFieldID() {
		return this.attributes.optionCode;
	}

	/**
	 * Validate the fields
	 *
	 * @since 2.0
	 * @param {Boolean} renderError - TRUE if you want to render the field errors, FALSE if you only want to return the response object
	 * @return {object} Object where the key is the field ID and the value is a validateField object
	 */
	validate( renderError = true ) {

		// Check and apply dynamic visibility rules// Check and apply dynamic visibility rules
		if ( this.attributes.dynamic_visibility ) {
			this.applyDynamicVisibility();
		}

		// Create validation object
		const validation = {
			isValid: true,
			message: '',
		};

		// Create array to save the validation of the rows
		const rowsValidation = [];

		// Iterate fields
		this.fieldRows.forEach( ( row ) => {
			// Create object to save the row validation
			const rowValidation = {};

			// Iterate each field
			row.forEach( ( fieldInstance ) => {
				// Validate and add response to the validation object
				rowValidation[ fieldInstance.getFieldID() ] = fieldInstance.validate( renderError );

				// Update global validation
				validation.isValid = validation.isValid && rowValidation[ fieldInstance.getFieldID() ].isValid;
			} );

			// Add row validation to the main validation array
			rowsValidation.push( rowValidation );
		} );

		// Return validation object
		return validation;
	}

	initTokenSelector() {
		this.fieldRows.forEach( ( rowFields ) => {
			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				fieldInstance.initTokenSelector();
			} );
		} );
	}

	destroyTokenSelector() {
		this.fieldRows.forEach( ( rowFields ) => {
			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				fieldInstance.destroyTokenSelector();
			} );
		} );
	}

	/**
	 * Create date/time selector
	 */
	initFlatpickr() {
		// Iterate rows
		this.fieldRows.forEach( ( rowFields ) => {
			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				fieldInstance.initFlatpickr();
			} );
		} );
	}

	/**
	 * Destroys the date/time selectors that were created before for
	 * this repeater field
	 */
	destroyFlatpickr() {
		// Iterate rows
		this.fieldRows.forEach( ( rowFields ) => {
			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				fieldInstance.destroyFlatpickr();
			} );
		} );
	}

	initCodeMirror() {
		this.fieldRows.forEach( ( rowFields ) => {
			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				fieldInstance.initCodeMirror();
			} );
		} );
	}

	destroyCodeMirror() {
		this.fieldRows.forEach( ( rowFields ) => {
			// Iterate row fields
			rowFields.forEach( ( fieldInstance ) => {
				// Add field
				fieldInstance.destroyCodeMirror();
			} );
		} );
	}

	/**
	 * Generates the template for a header row in a table.
	 *
	 * @param {Array} headerMatrix - An array of header objects. Each object contains properties like 'id', 'title', 'description', 'isRequired', and 'isHidden'.
	 * @returns {Node} - The jQuery object representing the header row element.
	 */
	_templateHeader( headerMatrix ) {
		// Create HTML of the heading
		const $headerRow = $( `<tr></tr>` );

		// Iterate the header
		headerMatrix.forEach( header => {
			// Create column
			const $headingColumn = $( 
				`<th 
					class="form-repeater-fields__table-heading-cell ${ header.attributes?.isHidden ? 'form-repeater-fields__table-heading-cell--hidden' : '' }" 
					data-id="${ header.attributes?.id }"
				></th>`
			);

			// Add the content
			$headingColumn.append( header.node );

			// Add heading column
			$headerRow.append( $headingColumn );
		} );

		return $headerRow;
	}

	/**
	 * Generates the template for a heading cell in a table.
	 *
	 * @param {Object} header - The header object containing properties like 'id', 'title', 'description', 'isRequired', and 'isHidden'.
	 * @param {String} header.id - The ID of the header.
	 * @param {String} header.title - The title of the header.
	 * @param {String} header.description - The description of the header.
	 * @param {Boolean} header.isRequired - Indicates whether the header is required.
	 * @param {Boolean} header.isHidden - Indicates whether the header is hidden.
	 * @returns {Node} - The jQuery object representing the heading cell element.
	 */
	_templateHeadingCell( header ) {
		// Create column
		const $headingColumn = $( `<div data-id="${ header.id }"></div>` );

		// Add the title
		if ( ! isEmpty( header.title ) ) {
			$headingColumn.append(
				$( `
				<div class="form-repeater-fields__table-heading-cell-title">
					${ header.title }
					${ header.isRequired ? '<span class="form-repeater-fields__table-heading-cell-required"></span>' : '' }
				</div>
			` )
			);
		} else if ( ! isEmpty( this.attributes.ajax ) ) {
			$headingColumn.append(
				$( `
				<div class="form-repeater-fields__table-heading-cell-title">
					<span class="uap-placeholder-text" data-placeholder="Label"></span> <span class="uap-placeholder-text" data-placeholder="Second"></span>
				</div>
			` )
			);
		}

		// Add the description
		if ( ! isEmpty( header.description ) ) {
			$headingColumn.append(
				$( `
				<div class="form-repeater-fields__table-heading-cell-description">
					${ header.description }
				</div>
			` )
			);
		}

		return $headingColumn;
	}

	/**
	 * Generates the template for rows in a table.
	 *
	 * @param {Array} tableMatrixRows - An array of row objects. Each object represents a row and contains field instances.
	 * @returns {Array<Node>} - An array of jQuery objects, each representing a row element.
	 */
	_templateRows( tableMatrixRows ) {
		const $rows = [];

		// Iterate the rows
		tableMatrixRows.forEach( ( row, index ) => {
			// Create container for the row
			const $row = $( `<tr data-order="${ index }"></tr>` );

			row.forEach( rowCell => {
				const $td = $( `<td 
					class="form-repeater-fields__table-rows-cell ${ rowCell.attributes.isHidden ? 'form-repeater-fields__table-rows-cell--hidden' : '' }" 
					data-id="${ rowCell.attributes.id }"
				></td>` );

				$td.append( rowCell.node );

				return $row.append( $td );
			} );

			// Add $row to the $rows element
			$rows.push( $row );
		} );

		return $rows;
	}

	/**
	 * Generates the template for a cell in a table.
	 *
	 * @param {Field} fieldInstance - The instance of the field
	 * @returns {Node} - The jQuery object representing the cell element.
	 */
	_templateCell( fieldInstance ) {
		// Create container
		const $fieldCell = $( `<div data-id="${ fieldInstance.attributes.optionCode }"></div>` );

		// Get field's DOM element
		const $field = fieldInstance.getFormElement();
		// Make sure all the libraries had been initialized
		fieldInstance.initLibraries();

		// Add field container
		$fieldCell.append( $field );

		return $fieldCell;
	}

	/**
	 * Applies dynamic visibility rules to the field.
	 */
	applyDynamicVisibility() {
		const visibility = this.attributes.dynamic_visibility;
		let isVisible = visibility.default_state === 'visible';

		// Iterate over each visibility rule
		visibility.visibility_rules.forEach( rule => {
			const ruleResult = this.evaluateVisibilityRule( rule );
			if ( ruleResult ) {
				isVisible = rule.resulting_visibility === 'show';
			}
		} );

		// Update field visibility based on evaluated rules
		this.updateFieldVisibility( isVisible );
	}

	/**
	 * Evaluates a single visibility rule.
	 * @param {Object} rule - The visibility rule to evaluate.
	 * @return {Boolean} - Result of the rule evaluation.
	 */
	evaluateVisibilityRule( rule ) {
		return rule.rule_conditions[ rule.operator === 'AND' ? 'every' : 'some' ]( condition => {
			const fieldValue = getField( this.attributes.itemId, condition.option_code, this.attributes.itemType ).getValue();
			return this.compareValues( fieldValue, condition.value, condition.compare );
		} );
	}

	/**
	 * Updates the visibility of the field.
	 * @param {Boolean} isVisible - Whether the field should be visible.
	 */
	updateFieldVisibility( isVisible ) {
		// Update the field's visibility attribute
		this.attributes.isHidden = ! isVisible;

		try {
			if ( isVisible ) {
				this.formElement.removeClass( 'form-element--hidden' );
			} else {
				this.formElement.addClass( 'form-element--hidden' );
			}
		} catch ( e ) {
			// console.warn( e );
		}
	}

	/**
	 * Compares two values based on a specified operator.
	 * @param {*} value1 - The first value to compare.
	 * @param {*} value2 - The second value to compare.
	 * @param {String} operator - The comparison operator.
	 * @return {Boolean} - Result of the comparison.
	 */
	compareValues( value1, value2, operator ) {
		switch ( operator ) {
			case '==': return value1 == value2;
			case '!=': return value1 != value2;
			// Add more comparison operators as needed
			default: return false;
		}
	}

	/**
	 * Sets up change listeners on parent fields that affect this field's visibility.
	 */
	setupVisibilityChangeListener() {
		const visibility = this.attributes.dynamic_visibility;

		visibility.visibility_rules.forEach( rule => {
			rule.rule_conditions.forEach( condition => {
				const parentField = getField( this.attributes.itemId, condition.option_code, this.attributes.itemType );
				if ( parentField ) {
					parentField.field.on( 'change', () => {
						this.applyDynamicVisibility();
					} );
				}
			} );
		} );
	}

	/**
	 * Returns the headers of the table. 
	 * If the layout is transposed, rows will be used as headers.
	 * 
	 * @returns {Array} The headers of the table
	 */
	get headers() {
		// Create the row number cell
		const $rowNumber = $( `<div data-id="_AUTOMATOR_ROW_NUMBER">#</div>` );

		// Create the actions cell
		const $actions = $( `<div data-id="_AUTOMATOR_TABLE_ACTIONS"></div>` );

		// Get the headers from the current value
		let dynamicHeaders = [];
		if ( isDefined( this.attributes.ajax ) && isEmpty( this.attributes.fields ) && ! isEmpty( this.fieldRows ) ) {
			// dynamicHeaders = this.fieldRows;
			dynamicHeaders = this.fieldRows[0]
				.filter( fieldInstance => {
					return ! fieldInstance.attributes.optionCode.endsWith( '_readable' );
				} )
				.map( ( fieldInstance, index ) => {
					const header = {
						title: '',
						id: fieldInstance.attributes.optionCode
					};

					return {
						type: 'heading',
						attributes: header,
						node: this._templateHeadingCell( header )
					};
				} );
		} else {
			// Otherwise get them from the fields
			dynamicHeaders = this.attributes.fields.map( fieldData => {
				const header = {
					title: isDefined( fieldData.label ) ? fieldData.label : '',
					description: isDefined( fieldData.description ) ? fieldData.description : '',
					isRequired: isDefined( fieldData.isRequired ) ? fieldData.isRequired : false,
					isHidden: isDefined( fieldData.isHidden ) ? fieldData.isHidden : false,
					id: fieldData.optionCode
				};

				return {
					type: 'heading',
					attributes: header,
					node: this._templateHeadingCell( header )
				};
			} );
		}

		// Create the headers
		const headers = [
			// Row number
			{
				type: 'heading',
				attributes: {
					id: '_AUTOMATOR_ROW_NUMBER',
					title: '#',
				},
				node: $rowNumber
			},
			// Table headers
			...dynamicHeaders,
		];

		if ( ! this.attributes.hideActions ) {
			// Table actions column
			headers.push( {
				type: 'heading',
				attributes: {
					id: '_AUTOMATOR_TABLE_ACTIONS',
					title: '',
				},
				node: $actions
			} );
		}

		return headers;
	}

	/**
	 * Generates an array of rows for the table.
	 *
	 * @returns {Array} - An array of rows. Each row is an array of objects, each representing a cell in the row. Each object has a 'type', 'attributes' (optional), and 'node' property.
	 */
	get rows() {
		return [
			...this.fieldRows.map( ( rowFields, index ) => {
				return this.getRow( rowFields, index )
			} )
		];
	}

	/**
	 * Generates a row for a table.
	 *
	 * @param {Array} rowFields - An array of field instances for the row.
	 * @param {Number} index - The index of the row.
	 * @returns {Array} - An array of objects, each representing a cell in the row. Each object has a 'type', 'attributes' (optional), and 'node' property.
	 */
	getRow( rowFields, index ) {
		// Create the row number cell
		const $rowNumber = $( `<div data-id="_AUTOMATOR_ROW_NUMBER">${ ( index + 1 ) }</div>` );

		// Create the actions cell
		const $actions = $( `<div data-id="_AUTOMATOR_TABLE_ACTIONS"></div>` );

		// Get number of rows
		const numberOfRows = this.fieldRows.length;

		const fieldAtts = this.getAttributes();
		// Check if the field requires at least on row and if this is the first row
		if ( index != 0 || ( index == 0 && ( ! this.attributes.isRequired || numberOfRows > 1 ) ) ) {
			// Check if there is a custom "Remove row" label. If there is one,
			// use that, otherwise use the default one
			const removeRowLabel = isDefined( this.attributes.removeRowButtonLabel )
				? this.attributes.removeRowButtonLabel
				: escapeHTML( __( 'Remove row', 'uncanny-automator' ) );

			// Create delete button
			const $removeRowButton = $(
				`<div class="form-repeater-fields__table-rows-cell-remove" uap-tooltip="${ removeRowLabel }"><span></span></div>`
			);

			// Listen clicks to the remove button
			$removeRowButton.on( 'click', () => {
				// Remove the row by index
				this.deleteRow( index );
			} );

			// if hideActions is false, Add Remove row button
			if ( ! fieldAtts.hideActions ) {
				// Add remove button to the actions cell
				$actions.append( $removeRowButton );
			}
		}
		
		// Create rows
		const rows = [
			// Row number
			{
				type: 'row-cell',
				attributes: {
					id: '_AUTOMATOR_ROW_NUMBER',
					title: ( index + 1 ),
				},
				node: $rowNumber
			},
			// Table fields
			...rowFields.map( fieldInstance => {
				return {
					type: 'row-cell',
					attributes: {
						id: fieldInstance.attributes.optionCode,
						isHidden: fieldInstance.attributes.isHidden
					},
					node: this._templateCell( fieldInstance )
				};
			} )
		];

		if ( ! this.attributes.hideActions ) {
			// Table actions column
			rows.push( {
				type: 'row-cell',
				attributes: {
					id: '_AUTOMATOR_TABLE_ACTIONS',
					title: '',
				},
				node: $actions
			} );
		}

		return rows;
	}

	/**
	 * Gets the table matrix with predefined data. This method is typically used to retrieve
	 * a static 2D array representing a table structure where the first row contains headers,
	 * and subsequent rows contain data entries along with action identifiers.
	 * 
	 * @example
	 * // Calling the getter would return an array like this:
	 * [
	 *     [ "#", "Column 1", "Column 2", "Column 3", "TABLE_ACTIONS" ],
	 *     [ "Row 1", "(1,1)", "(1,2)", "(1,3)", "TABLE_ACTIONS" ],
	 *     [ "Row 2", "(2,1)", "(2,2)", "(2,3)", "TABLE_ACTIONS" ],
	 *     [ "Row 3", "(3,1)", "(3,2)", "(3,3)", "TABLE_ACTIONS" ]
	 * ]
	 * 
	 * @return {Array<Array>} The 2D array with headers and row data including action controls.
	 */
	get tableMatrix() {
		return [
			[ ...this.headers ],
			...this.rows
		];
	}

	/**
	 * Transposes a 2D array, converting rows to columns and columns to rows.
	 * This function takes a matrix of data and reorients it so that rows become columns and
	 * vice versa, which is particularly useful for operations on matrices in algorithms that
	 * require such transformations.
	 * 
	 * @param {Array<Array>} matrix The 2D array to be transposed.
	 * 
	 * @example
	 * // Input matrix
	 * [
	 *     [ "#", "Column 1", "Column 2", "Column 3", "TABLE_ACTIONS" ],
	 *     [ "Row 1", "(1,1)", "(1,2)", "(1,3)", "TABLE_ACTIONS" ],
	 *     [ "Row 2", "(2,1)", "(2,2)", "(2,3)", "TABLE_ACTIONS" ],
	 *     [ "Row 3", "(3,1)", "(3,2)", "(3,3)", "TABLE_ACTIONS" ],
	 * ];
	 * 
	 * // Transposed matrix
	 * [
	 *     [ "#", "Row 1", "Row 2", "Row 3" ],
	 *     [ "Column 1", "(1,1)", "(2,1)", "(3,1)" ],
	 *     [ "Column 2", "(1,2)", "(2,2)", "(3,2)" ],
	 *     [ "Column 3", "(1,3)", "(2,3)", "(3,3)" ],
	 *     [ "TABLE_ACTIONS", "TABLE_ACTIONS", "TABLE_ACTIONS", "TABLE_ACTIONS" ]
	 * ]
	 * 
	 * @return {Array<Array>} The transposed 2D array where each row is now a column from the original array.
	 */
	_transposeMatrix( matrix ) {
		return matrix[ 0 ].map( 
			( _, colIndex ) => matrix.map( row => row[ colIndex ] )
		);
	};
}

export default FieldsRepeater;
